<template>
  <div class="talentspace-occupation-select" @keydown.esc="close">
    <label class="visually-hidden" :for="inputId">{{ placeholderText }}</label>
    <div class="input-group">
      <span class="input-group-text ts-search-icon"><i class="bi" :class="icon" aria-hidden="true"></i></span>
      <input
        :id="inputId"
        ref="input"
        v-model="query"
        class="form-control"
        type="search"
        autocomplete="off"
        role="combobox"
        :placeholder="placeholderText"
        :aria-expanded="String(isOpen)"
        :aria-controls="listId"
        :aria-activedescendant="activeIndex >= 0 ? `${listId}-${activeIndex}` : null"
        @input="onInput"
        @focus="hasFocus = true"
        @blur="onBlur"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="pickActive"
      />
      <!-- small mark with the number of chosen occupations, visible while the list covers the chips -->
      <span v-if="selected.length" class="input-group-text ts-occupation-count" :title="selectedLabel">
        <i class="bi bi-check2 me-1" aria-hidden="true"></i>{{ selected.length }}
        <span class="visually-hidden">{{ selectedLabel }}</span>
      </span>
    </div>

    <div v-if="isOpen" :id="listId" class="list-group talentspace-occupation-matches shadow-sm" role="listbox">
      <div v-if="loading" class="list-group-item text-body-secondary">
        <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>{{ loadingLabel }}
      </div>
      <div v-else-if="!rows.length" class="list-group-item text-body-secondary">{{ translations[9506] }}</div>
      <template v-for="(row, index) in rows" v-else>
        <!-- a group row selects every occupation of the group (as the legacy select did) -->
        <button
          :id="`${listId}-${index}`"
          :key="row.key"
          class="list-group-item list-group-item-action d-flex justify-content-between gap-3"
          :class="{ active: index === activeIndex, 'is-selected': row.selected, 'ts-occupation-group-row': row.isGroup }"
          type="button"
          role="option"
          :aria-selected="String(row.selected)"
          @mousedown.prevent
          @click="pick(row)"
        >
          <!-- option names come from the API with the typed text marked as <strong>…</strong> -->
          <span v-if="row.isGroup">{{ row.label }}</span>
          <span v-else v-html="row.label"></span>
          <span class="d-inline-flex align-items-center gap-2 flex-shrink-0">
            <span v-if="!row.isGroup" class="ts-filter-count small">{{ formatCount(row.count) }} {{ unit }}</span>
            <i class="bi bi-check2 ts-occupation-check" :class="{ invisible: !row.selected }" aria-hidden="true"></i>
          </span>
        </button>
      </template>
    </div>

    <div v-if="selected.length" class="filter-tag-list mt-2" :aria-label="placeholderText">
      <ts-filter-chip v-for="option in selected" :key="option.id" :label="plain(option.name || option.title)" @remove="remove(option)" />
    </div>

    <!-- Suggestions of related occupations after a selection (jobnews/similar_profession) -->
    <div v-if="similarProfessions && similarItems.length" class="alert alert-warning small mt-2 mb-0" role="status">
      <span v-html="translations[14019]"></span>
      <ul class="list-unstyled mt-2 mb-0">
        <li v-for="item in similarItems" :key="item.id">
          <a href="#" class="fw-bold" @click.prevent="addSimilar(item)">
            <i class="bi bi-arrow-repeat me-1" aria-hidden="true"></i>{{ item.name }} ({{ item.count }})
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { similarProfession } from '@/api/Profession';
import { formatCount } from '@/helpers/formatCount';
import {
  normalizeProfessionId, ProfessionGroup, ProfessionOption, queryProfessions,
} from '@/helpers/professionQuery';
import TsFilterChip from '@/components/Talentspace/blocks/TsFilterChip.vue';

interface Row {
  key: string
  label: string
  isGroup: boolean
  selected: boolean
  count: number
  group: ProfessionGroup
  option?: ProfessionOption
}

let uid = 0;

// Occupation search of the Talentspace design: a plain search field, matches below it and
// the selection as removable chips. Same v-model as SelectProfessionUpdate: an array of
// { id, name, title, count }, plus a 'change' event after every change.
@Component({
  components: { TsFilterChip },
})
export default class TsOccupationSelect extends Vue {
  @Prop({ default: () => [] }) readonly value!: ProfessionOption[] | null;

  @Prop({ default: '' }) readonly placeholder!: string;

  @Prop({ default: 'bi-search' }) readonly icon!: string;

  @Prop({ type: Boolean, default: false }) readonly similarProfessions!: boolean;

  @Prop({ type: Boolean, default: false }) readonly isApplicantSearch!: boolean;

  @Prop({ type: Boolean, default: false }) readonly courses!: boolean;

  inputId = `ts-occupation-${uid += 1}`;

  listId = `${this.inputId}-list`;

  query = '';

  groups: ProfessionGroup[] = [];

  loading = false;

  hasFocus = false;

  activeIndex = -1;

  similarItems: { id: string, name: string, count: number }[] = [];

  formatCount = formatCount;

  get translations() {
    return AppModule.translation;
  }

  get placeholderText() {
    return this.placeholder || `${this.translations[13588]}...`;
  }

  get loadingLabel() {
    return AppModule.translation[16555];
  }

  get unit() {
    if (this.isApplicantSearch) return this.translations[13769];
    return this.courses ? this.translations[15404] : this.translations[2983];
  }

  get selected(): ProfessionOption[] {
    return (this.value || []).map((option) => ({ ...option, id: normalizeProfessionId(option.id) }));
  }

  // The API answers from 3 characters on
  get isOpen() {
    return this.hasFocus && this.query.trim().length > 2;
  }

  // Flat list for rendering and keyboard navigation; chosen occupations stay in it with a check mark
  get rows(): Row[] {
    const chosen = new Set(this.selected.map((option) => option.id));
    return this.groups.flatMap((group) => [
      {
        key: `group-${group.group}`,
        label: group.group,
        isGroup: true,
        selected: group.libs.every((option) => chosen.has(option.id)),
        count: 0,
        group,
      },
      ...group.libs.map((option) => ({
        key: `option-${option.id}`,
        label: option.name,
        isGroup: false,
        selected: chosen.has(option.id),
        count: option.count,
        group,
        option,
      })),
    ]);
  }

  get selectedLabel() {
    return `${this.selected.length} ${AppModule.translation[16556]}`;
  }

  plain(html = '') {
    return html.replace(/<[^>]+>/g, '');
  }

  async onInput() {
    this.activeIndex = -1;
    const query = this.query.trim();
    if (query.length < 3) {
      this.groups = [];
      return;
    }
    this.loading = true;
    try {
      const groups = await queryProfessions(query, { isApplicantSearch: this.isApplicantSearch, courses: this.courses });
      // null: superseded by a newer query, which updates the list itself
      if (groups === null) return;
      this.groups = groups;
      this.loading = false;
    } catch (error) {
      console.error(error);
      this.loading = false;
    }
  }

  onBlur() {
    this.hasFocus = false;
    this.activeIndex = -1;
  }

  close() {
    this.query = '';
    this.groups = [];
  }

  move(step: number) {
    if (!this.rows.length) return;
    const last = this.rows.length - 1;
    this.activeIndex = this.activeIndex + step > last ? 0 : Math.max(-1, this.activeIndex + step);
  }

  pickActive() {
    const row = this.rows[this.activeIndex];
    if (row) this.pick(row);
  }

  // Clicking toggles: an occupation on/off, a group row all of its occupations at once.
  // The list stays open with the same search, so several can be picked in a row.
  pick(row: Row) {
    const ids = new Set((row.isGroup ? row.group.libs : [row.option as ProfessionOption]).map((option) => option.id));
    if (row.selected) {
      this.update(this.selected.filter((option) => !ids.has(option.id)));
      return;
    }
    const chosen = new Set(this.selected.map((option) => option.id));
    const added = (row.isGroup ? row.group.libs : [row.option as ProfessionOption]).filter((option) => !chosen.has(option.id));
    this.update([...this.selected, ...added]);
  }

  remove(option: ProfessionOption) {
    this.update(this.selected.filter((item) => item.id !== option.id));
  }

  addSimilar(item: { id: string, name: string, count: number }) {
    this.update([...this.selected, {
      id: normalizeProfessionId(item.id), name: item.name, title: item.name, count: item.count,
    }]);
  }

  update(value: ProfessionOption[]) {
    this.$emit('input', value);
    this.$emit('change');
    this.loadSimilar(value);
  }

  async loadSimilar(value: ProfessionOption[]) {
    if (!this.similarProfessions) return;
    const ids = value.map((option) => option.id).join(',');
    this.similarItems = ids
      ? await similarProfession({ sid: AppModule.languageSid, id: ids, ...(this.isApplicantSearch && { applicants_search: 1 }) })
      : [];
  }
}
</script>
